import { useState, useEffect, useRef } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Send, User, MessageCircle, Clock, Search, Check, CheckCheck, Trash2, ShieldAlert } from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const getWsBase = () => {
  if (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== 'RELATIVE') {
    return process.env.REACT_APP_BACKEND_URL.replace('http', 'ws');
  }
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}`;
};
const WS_BASE = getWsBase();
const WS_URL = `${WS_BASE}/ws`;

export default function AdminChat() {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [socket, setSocket] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [appVersion, setAppVersion] = useState('unknown');
  
  const scrollRef = useRef(null);
  const socketRef = useRef(null);
  const selectedIdRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const token = localStorage.getItem('token');

  // Sincronizar Ref con estado para que los listeners tengan el ID actual
  useEffect(() => {
    selectedIdRef.current = selectedSession?._id;
    console.log('📍 [AdminChat] Sesión seleccionada:', selectedSession?._id);
    if (selectedSession) {
      fetchHistory(selectedSession._id);
      setSessions(prev => prev.map(s => s._id === selectedSession._id ? { ...s, unread_count: 0 } : s));
    }
  }, [selectedSession?._id]);

  // Procesador central de mensajes del chat
  const processMessage = (data) => {
    if (!data) return;

    if (data.type === 'new_message') {
      const { session_id, message } = data;
      
      // 1. Actualizar lista lateral siempre
      setSessions(prev => {
        const idx = prev.findIndex(s => String(s._id) === String(session_id));
        const isSelected = selectedIdRef.current && String(session_id) === String(selectedIdRef.current);
        
        if (idx !== -1) {
          const updated = [...prev];
          const session = { ...updated[idx] };
          session.last_message = message.message;
          session.last_timestamp = message.timestamp;
          session.sender = message.sender;
          if (!isSelected && message.sender === 'user') {
            session.unread_count = (session.unread_count || 0) + 1;
          }
          updated.splice(idx, 1);
          return [session, ...updated];
        } else {
          console.log('✨ [AdminChat] Nueva sesión detectada vía socket');
          setTimeout(fetchSessions, 100);
          return prev;
        }
      });

      // 2. Inyectar mensaje si estamos en esa sesión
      if (selectedIdRef.current && String(session_id) === String(selectedIdRef.current)) {
         setMessages(prev => {
            const isDup = prev.some(m => 
              (m.id && m.id === message.id) || 
              (m.message === message.message && m.sender === message.sender && Math.abs(new Date(m.timestamp) - new Date(message.timestamp)) < 2000)
            );
            if (isDup) return prev;
            return [...prev, message].sort((a,b) => new Date(a.timestamp) - new Date(b.timestamp));
         });
         
         if (message.sender === 'user' && socketRef.current?.readyState === WebSocket.OPEN) {
           socketRef.current.send(JSON.stringify({ type: 'read_receipt', session_id }));
         }
      }
    } else if (data.type === 'user_online') {
      fetchSessions();
    } else if (data.type === 'read_receipt') {
      if (selectedIdRef.current && String(data.session_id) === String(selectedIdRef.current)) {
        setMessages(prev => prev.map(m => m.sender === 'admin' ? { ...m, is_read: true } : m));
      }
    }
  };

  useEffect(() => {
    const handleGlobalChatEvent = (event) => {
      console.log('📥 [AdminChat] Evento de AdminLayout recibido:', event.detail.type);
      processMessage(event.detail);
    };

    window.addEventListener('admin-chat-event', handleGlobalChatEvent);
    return () => window.removeEventListener('admin-chat-event', handleGlobalChatEvent);
  }, []);

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const res = await axios.get(`${API}/version`);
        if (res?.data?.version) {
          setAppVersion(res.data.version);
        }
      } catch (error) {
        console.warn('No se pudo obtener la versión de la app', error);
      }
    };

    fetchVersion();
  }, []);

  const fetchHistory = async (sessionId) => {
    if (!sessionId) return;
    try {
      const res = await axios.get(`${API}/chat/history/${sessionId}`);
      setMessages(res.data.sort((a,b) => new Date(a.timestamp) - new Date(b.timestamp)));
      console.log('📚 [AdminChat] Historial cargado para:', sessionId);
    } catch (err) {
      console.error('❌ Error cargando historial:', err);
    }
  };

  const fetchSessions = async () => {
    try {
      const res = await axios.get(`${API}/chat/active-sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(res.data);
      console.log('🔄 [AdminChat] Sesiones actualizadas');
    } catch (err) {
      console.error('❌ Error cargando sesiones:', err);
    }
  };

  // Conexión del socket de envío
  useEffect(() => {
    const connect = () => {
      if (socketRef.current?.readyState === WebSocket.OPEN) return;
      
      const ws = new WebSocket(`${WS_URL}/admin/chat`);
      ws.onopen = () => {
        console.log('🔌 [AdminChat] Socket Conectado');
        setIsConnected(true);
        socketRef.current = ws;
        setSocket(ws);
        fetchSessions();
        
        if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' }));
        }, 20000);
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'pong') return;
          console.log('🔌 [AdminChat Socket] Mensaje (Redundancia):', data.type);
          
          // Procesar directamente para máxima fiabilidad si el evento global fallara
          processMessage(data);
        } catch (e) {}
      };

      ws.onclose = () => {
        setIsConnected(false);
        socketRef.current = null;
        setSocket(null);
        if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
        reconnectTimeoutRef.current = setTimeout(connect, 3000);
      };
    };

    connect();
    return () => {
      if (socketRef.current) socketRef.current.close();
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSend = (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || !selectedSession || !socket) return;
    
    const targetId = selectedSession._id;
    const msgData = {
      type: 'reply',
      session_id: targetId,
      message: inputMessage
    };
    
    socket.send(JSON.stringify(msgData));

    // Optimismo: añadir localmente
    const localMsg = {
      sender: 'admin',
      message: inputMessage,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, localMsg]);
    setInputMessage('');
    
    setSessions(prev => prev.map(s => String(s._id) === String(targetId) ? {
      ...s,
      last_message: inputMessage,
      last_timestamp: localMsg.timestamp,
      sender: 'admin'
    } : s));
  };

  const deleteSession = async (sessionId) => {
    if (!sessionId) {
      toast.error('No se pudo identificar la sesión');
      return;
    }
    if (!window.confirm('¿Estás seguro de que quieres borrar TODA la conversación? Esta acción no se puede deshacer.')) return;
    try {
      await axios.delete(`${API}/chat/session/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Conversación eliminada');
      setSessions(prev => prev.filter(s => s._id !== sessionId));
      if (selectedSession?._id === sessionId) setSelectedSession(null);
    } catch (err) {
      console.error('Error borrando sesión:', err);
      toast.error('Error al borrar sesión');
    }
  };

  const deleteMessage = async (msgId, idx) => {
    // Intentar ID real o fallback a timestamp para el estado local
    const idToDelete = msgId || messages[idx].timestamp;
    if (!idToDelete) return;

    if (!window.confirm('¿Borrar este mensaje?')) return;
    try {
      await axios.delete(`${API}/chat/message/${idToDelete}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Mensaje borrado');
      setMessages(prev => prev.filter((m, i) => (m.id && m.id !== idToDelete) || (!m.id && i !== idx)));
    } catch (err) {
      console.error('Error borrando mensaje:', err);
      toast.error('Error al borrar mensaje');
    }
  };

  const filteredSessions = sessions.filter(s => 
    (s.user_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (s._id || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="h-[calc(100vh-140px)] flex flex-col md:flex-row gap-6 p-4">
        {/* Session List */}
        <div className="w-full md:w-80 flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-slate-800">Mensajería</h2>
            <div className="flex flex-col items-end gap-1">
              <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-medium ${
                isConnected ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500 animate-pulse'}`}></span>
                {isConnected ? 'En línea' : 'Desconectado'}
              </div>
              <span className="text-[9px] text-slate-400 font-mono">{appVersion}</span>
            </div>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar conversación..."
              className="pl-9 bg-white border-slate-200"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <ScrollArea className="flex-1 rounded-xl border bg-slate-50 shadow-inner">
            <div className="p-2 space-y-2">
              {filteredSessions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  No hay conversaciones
                </div>
              ) : filteredSessions.map((session) => (
                <button
                  key={session._id}
                  onClick={() => setSelectedSession(session)}
                  className={`w-full text-left p-3 rounded-lg transition-all duration-200 border flex flex-col gap-1 relative overflow-hidden ${
                    selectedSession?._id === session._id 
                      ? 'bg-primary/10 border-primary/30 shadow-sm' 
                      : 'bg-white border-transparent hover:bg-slate-50 hover:border-slate-200'
                  }`}
                >
                  {session.unread_count > 0 && session._id !== selectedSession?._id && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-destructive" />
                  )}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 max-w-[70%]">
                      <span className="font-bold text-sm truncate text-slate-800">
                        {session.user_name || 'Usuario Anónimo'}
                      </span>
                      {/* Indicador de actividad real */}
                      {(session.is_active || (session.last_timestamp && (new Date() - new Date(session.last_timestamp)) < 300000)) && (
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" title="Activo recientemente"></span>
                      )}
                    </div>
                    <span className="text-[10px] font-medium text-slate-400">
                      {session.last_timestamp && format(new Date(session.last_timestamp), 'HH:mm', { locale: es })}
                    </span>
                  </div>
                  {session.user_phone && (
                    <span className="text-[10px] text-primary font-medium">{session.user_phone}</span>
                  )}
                  <div className="flex items-center justify-between gap-2 mt-0.5">
                    <p className={`text-xs truncate flex-1 ${
                      session.unread_count > 0 && session._id !== selectedSession?._id 
                        ? 'text-slate-900 font-semibold' 
                        : 'text-slate-500'
                    }`}>
                      {session.sender === 'admin' ? '✓ ' : ''}
                      {session.last_message || <span className="italic opacity-50">Mensaje vacío</span>}
                    </p>
                    {session.unread_count > 0 && session._id !== selectedSession?._id && (
                      <Badge className="bg-destructive hover:bg-destructive text-[10px] h-4.5 min-w-[18px] px-1 flex items-center justify-center rounded-full animate-bounce">
                        {session.unread_count}
                      </Badge>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </ScrollArea>
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col rounded-2xl border bg-slate-50 overflow-hidden shadow-sm relative">
          {selectedSession ? (
            <>
              {/* Chat Header */}
              <div className="bg-white border-b px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{selectedSession.user_name || 'Usuario Anónimo'}</h3>
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                      <span className="text-xs text-muted-foreground">Chat activo</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    ID: {selectedSession._id}
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="text-destructive hover:text-destructive hover:bg-destructive/10 h-8 w-8"
                    onClick={() => deleteSession(selectedSession._id)}
                    title="Borrar conversación completa"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Messages Area */}
              <ScrollArea className="flex-1 p-6">
                <div className="space-y-4">
                  {messages.map((m, idx) => (
                    <div 
                      key={m.id || idx}
                      className={`flex group ${m.sender === 'admin' ? 'justify-end' : 'justify-start'}`}
                    >
                      {/* Botón borrar mensaje individual al hacer hover */}
                      {m.sender !== 'admin' && (
                         <button 
                            onClick={() => deleteMessage(m.id, idx)}
                            className="opacity-0 group-hover:opacity-100 p-1 mr-1 text-slate-300 hover:text-destructive transition-all"
                            title="Borrar mensaje"
                         >
                            <Trash2 className="w-3 h-3" />
                         </button>
                      )}
                      
                      <div className={`max-w-[70%] p-3 rounded-2xl shadow-sm relative ${
                        m.sender === 'admin' 
                          ? 'bg-primary text-white rounded-tr-none' 
                          : 'bg-white text-slate-800 rounded-tl-none border'
                      }`}>
                        <p className="text-sm leading-relaxed">
                          {m.message || (
                            <span className="italic opacity-50 flex items-center gap-1.5 py-1">
                              <ShieldAlert className="w-3.5 h-3.5" />
                              Mensaje no legible o vacío
                            </span>
                          )}
                        </p>
                        <div className="flex items-center justify-end gap-1 mt-1">
                          <span className={`text-[10px] block ${
                            m.sender === 'admin' ? 'text-primary-foreground/70' : 'text-slate-400'
                          }`}>
                            {format(new Date(m.timestamp), 'HH:mm', { locale: es })}
                          </span>
                          {m.sender === 'admin' && (
                            <div className="flex items-center gap-1">
                              {m.is_read ? (
                                <CheckCheck className="w-3" />
                              ) : (
                                <Check className="w-3 text-primary-foreground/50" />
                              )}
                              <button 
                                onClick={() => deleteMessage(m.id, idx)}
                                className="opacity-0 group-hover:opacity-100 ml-1 text-primary-foreground/50 hover:text-white"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={scrollRef} />
                </div>
              </ScrollArea>

              {/* Chat Input */}
              <div className="p-4 border-t bg-white">
                <form onSubmit={handleSend} className="flex items-center gap-2">
                  <Input
                    placeholder="Escribe tu respuesta..."
                    className="flex-1 border-slate-200 focus-visible:ring-primary text-sm h-11"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                  />
                  <Button 
                    type="submit" 
                    size="icon" 
                    className="h-11 w-11 shrink-0 shadow-lg"
                    disabled={!inputMessage.trim()}
                  >
                    <Send className="h-5 w-5" />
                  </Button>
                </form>
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-8 text-muted-foreground">
              <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                <MessageCircle className="w-10 h-10" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800">Chat en Vivo</h3>
              <p className="max-w-xs mt-2">
                Selecciona una conversación a la izquierda para empezar a chatear con los visitantes de tu web en tiempo real.
              </p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
